import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SecondaryHeading from "../../components/common/Typography/SecondaryHeading";
import Heading from "../../components/common/Typography/Heading";
import AuxiliaryText from "../../components/common/Typography/AuxiliaryText";
import SpecialText from "../../components/common/Typography/SpecialText";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const EstimatedHighToLowCostGraph = () => {
  const containerRef = useRef(null);
  const highPriceBarRef = useRef(null);
  const mediumPriceBarRef = useRef(null);
  const lowPriceBarRef = useRef(null);

  useEffect(() => {
    // Set initial state - all progress bars start at 0% width
    gsap.set([highPriceBarRef.current, mediumPriceBarRef.current, lowPriceBarRef.current], {
      width: "0%"
    });

    // Create scroll trigger animations
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top 80%", // Start animation when top of element is 80% from top of viewport
        end: "bottom 20%", // End animation when bottom of element is 20% from top of viewport
        toggleActions: "play none none reverse", // Play on enter, reverse on leave
        markers: false, // Set to true for debugging
      }
    });

    // Animate progress bars sequentially
    tl.to(highPriceBarRef.current, {
      width: "98%",
      duration: 1.2,
      ease: "power2.out"
    })
    .to(mediumPriceBarRef.current, {
      width: "70%",
      duration: 1.2,
      ease: "power2.out"
    }, "-=0.8") // Start 0.8 seconds before previous animation ends
    .to(lowPriceBarRef.current, {
      width: "40%",
      duration: 1.2,
      ease: "power2.out"
    }, "-=0.8");

    // Cleanup function
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <>
      <>
        {/* Result Section */}
        <div className="tp-container-small max-w-3xl mx-auto h-[600px] flex flex-col justify-start pt-20">
          <div className="flex flex-col justify-center items-center gap-x-10">
            <div className="w-[100%]">
              <Heading variant={"h2"} className={"text-white text-left"}>
                Mixer Repair costs in Melbourne 3000
              </Heading>
            </div>
            <div ref={containerRef} className="w-[100%] rounded-xl py-8 pb-7 max-w-4xl mx-auto relative">
              
              {/* Progress Bars Section */}
              <div className="space-y-7 flex flex-col justify-center items-start">

                {/* High Price Progress Bar */}
                <div className="space-y-0 w-full">
                  <div className="flex items-center gap-4">
                    <div className="flex-1 rounded-full">
                      <div 
                        ref={highPriceBarRef}
                        className="h-3 mb-3 rounded-full"
                        style={{ 
                          background: 'linear-gradient(to right, #a88fff 0%, #8b6bff 50%, #2E00FF 100%)'
                        }}
                      ></div>
                      <AuxiliaryText variant={"Eyebrow"} className="text-white font-semibold">
                      High
                      </AuxiliaryText>
                    </div>
                    <SpecialText variant={"MetricMajor"} className="text-white font-medium whitespace-nowrap -mt-8">
                      $96
                    </SpecialText>
                  </div>
                </div>

                {/* Medium Price Progress Bar */}
                <div className="space-y-0 w-full">
                  <div className="flex items-center gap-4">
                    <div className="flex-1 rounded-full">
                      <div 
                        ref={mediumPriceBarRef}
                        className="h-3 mb-3 rounded-full"
                        style={{ 
                          background: 'linear-gradient(to right, #a88fff 0%, #8b6bff 50%, #2E00FF 100%)'
                        }}
                      ></div>
                      <AuxiliaryText variant={"Eyebrow"} className="text-white font-semibold">
                      Medium
                    </AuxiliaryText>
                    </div>
                    <SpecialText variant={"MetricMajor"} className="text-white font-medium whitespace-nowrap -mt-8">
                      $78
                    </SpecialText>
                  </div>
                </div>

                {/* Low Price Progress Bar */}
                <div className="space-y-0 w-full">
                  <div className="flex items-center gap-4">
                    <div className="flex-1 rounded-full">
                      <div 
                        ref={lowPriceBarRef}
                        className="h-3 mb-3 rounded-full"
                        style={{ 
                          background: 'linear-gradient(to right, #a88fff 0%, #8b6bff 50%, #2E00FF 100%)'
                        }}
                      ></div>
                      <AuxiliaryText variant={"Eyebrow"} className="text-white font-semibold">
                      Low
                    </AuxiliaryText>
                    </div>
                    <SpecialText variant={"MetricMajor"} className="text-white font-medium whitespace-nowrap -mt-8">
                      $50
                    </SpecialText>
                  </div>
                </div>

                <div className="mt-20 text-white">
                    <SpecialText variant={"FootNoteDisclaimer"}>
                      Additional Note
                    </SpecialText>
                    <SpecialText
                      variant={"FootNoteDisclaimer"}
                      className={"text-white mt-3"}
                    >
                      Remember, these estimates are based on average rates and
                      may vary. For updated prices and accurate quotes, please
                      contact businesses directly.
                    </SpecialText>
                  </div>

              </div>
            </div>
          </div>
        </div>
      </>
    </>
  );
};

export default EstimatedHighToLowCostGraph;
